import React, { useState, useEffect } from 'react';
import { Modal, Switch, Form, Divider, Button, Space, Typography, message } from 'antd';
import { SettingOutlined, ReloadOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { getWeekPlanSettings, setWeekPlanSettings, resetWeekPlanSettings } from '../../utils/cookieManager';
import styles from './WeekPlan.module.css';

const { Title, Text } = Typography;

const SettingsModal = ({ visible, onClose, onSettingsChange }) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [weekPlanSettings, setWeekPlanSettings] = useState({});
  const [loading, setLoading] = useState(false);

  // Load settings when modal opens
  useEffect(() => {
    if (visible) {
      // Load week plan settings from cookies
      const savedWeekPlanSettings = getWeekPlanSettings();
      setWeekPlanSettings(savedWeekPlanSettings);
      form.setFieldsValue(savedWeekPlanSettings);
    }
  }, [visible, form]);

  // Handle form value changes
  const handleValuesChange = (changedValues, allValues) => {
    // Update week plan settings (cookie-based)
    setWeekPlanSettings(allValues);
  };

  // Save settings
  const handleSave = () => {
    try {
      setLoading(true);

      // Get all form values
      const allValues = form.getFieldsValue();

      // Save week plan settings to cookies
      const cookieSuccess = setWeekPlanSettings(allValues);

      if (cookieSuccess) {
        message.success(t('settingsSaved'));

        // Notify parent component about settings change
        if (onSettingsChange) {
          onSettingsChange(allValues);
        }
        onClose();
      } else {
        message.error(t('failedToSaveSettings'));
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      message.error(t('errorSavingSettings'));
    } finally {
      setLoading(false);
    }
  };

  // Reset settings to default
  const handleReset = () => {
    Modal.confirm({
      title: t('confirmResetSettings'),
      content: t('resetSettingsWarning'),
      okText: t('reset'),
      cancelText: t('cancel'),
      onOk: () => {
        try {
          // Reset week plan settings
          resetWeekPlanSettings();
          const defaultWeekPlanSettings = getWeekPlanSettings();
          setWeekPlanSettings(defaultWeekPlanSettings);

          // Update form
          form.setFieldsValue(defaultWeekPlanSettings);

          message.success(t('settingsReset'));

          if (onSettingsChange) {
            onSettingsChange(defaultWeekPlanSettings);
          }
        } catch (error) {
          console.error('Error resetting settings:', error);
          message.error(t('errorResettingSettings'));
        }
      }
    });
  };

  // Handle modal cancel
  const handleCancel = () => {
    // Reset form to saved values
    const savedWeekPlanSettings = getWeekPlanSettings();
    form.setFieldsValue(savedWeekPlanSettings);
    onClose();
  };

  return (
    <Modal
      title={
        <Space>
          <SettingOutlined />
          {t('weekPlanSettings')}
        </Space>
      }
      open={visible}
      onCancel={handleCancel}
      width={600}
      footer={[
        <Button key="reset" icon={<ReloadOutlined />} onClick={handleReset}>
          {t('resetToDefault')}
        </Button>,
        <Button key="cancel" onClick={handleCancel}>
          {t('cancel')}
        </Button>,
        <Button key="save" type="primary" loading={loading} onClick={handleSave}>
          {t('saveSettings')}
        </Button>
      ]}
    >
      <Form
        form={form}
        layout="vertical"
        onValuesChange={handleValuesChange}
        initialValues={weekPlanSettings}
      >
        <Title level={4}>{t('displaySettings')}</Title>
        <Text type="secondary">{t('displaySettingsDescription')}</Text>
        
        <Divider />

        <Form.Item
          name="showSubOrganisation"
          valuePropName="checked"
          label={
            <Space direction="vertical" size={0}>
              <Text strong>{t('showSubOrganisation')}</Text>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                {t('showSubOrganisationDescription')}
              </Text>
            </Space>
          }
        >
          <Switch />
        </Form.Item>

        <Form.Item
          name="showComments"
          valuePropName="checked"
          label={
            <Space direction="vertical" size={0}>
              <Text strong>{t('showComments')}</Text>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                {t('showCommentsDescription')}
              </Text>
            </Space>
          }
        >
          <Switch />
        </Form.Item>

        <Form.Item
          name="showWorkDuration"
          valuePropName="checked"
          label={
            <Space direction="vertical" size={0}>
              <Text strong>{t('showWorkDuration')}</Text>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                {t('showWorkDurationDescription')}
              </Text>
            </Space>
          }
        >
          <Switch />
        </Form.Item>

        <Divider />

        <Title level={4}>{t('behaviorSettings')}</Title>
        <Text type="secondary">{t('behaviorSettingsDescription')}</Text>
        
        <Form.Item
          name="autoSave"
          valuePropName="checked"
          label={
            <Space direction="vertical" size={0}>
              <Text strong>{t('autoSave')}</Text>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                {t('autoSaveDescription')}
              </Text>
            </Space>
          }
        >
          <Switch />
        </Form.Item>

      </Form>
    </Modal>
  );
};

export default SettingsModal;
